import type { BusinessGetResponseType } from "@sellar-io/api-v2/types";
import type { ExtendedFirebaseUser } from "../../useFirebaseUser.shared";

type IdentityContext = { user: ExtendedFirebaseUser | undefined };
type BusinessContext = IdentityContext & {
  business: BusinessGetResponseType | null;
};

/** Scope is declared by the hook, independently of caller query options. */
type QueryScopeContext =
  | {
      scope: "public";
      user?: never;
      business?: never;
      token?: never;
    }
  | (IdentityContext & {
      scope: "user";
      business?: never;
      token?: never;
    })
  | (BusinessContext & {
      scope:
        | "optionalBusiness"
        | "businessIfUser"
        | "nonSupplier"
        | "business"
        | "buyer"
        | "supplier"
        | "businessMember"
        | "buyerMember"
        | "supplierMember";
      token?: never;
    })
  | {
      scope: "alternateToken";
      token: string | null | undefined;
      user?: never;
      business?: never;
    };

export type ApiV2QueryContext = QueryScopeContext & {
  [
    Key in
      | "requireUser"
      | "requireBusiness"
      | "requireBusinessMembership"
      | "requireBusinessType"
  ]?: never;
};

export const isApiV2ContextReady = (context: ApiV2QueryContext): boolean => {
  switch (context.scope) {
    case "public":
    case "optionalBusiness":
      return true;
    case "businessIfUser":
      return (
        !context.user ||
        (!!context.business?.id &&
          !!(context.business.Retailer?.id || context.business.Supplier?.id))
      );
    case "nonSupplier":
      return !context.business?.Supplier?.id;
    case "alternateToken":
      return !!context.token;
    case "user":
      return !!context.user;
    case "business":
      return !!context.user && !!context.business?.id;
    case "buyer":
      return (
        !!context.user &&
        !!context.business?.id &&
        !!context.business.Retailer?.id
      );
    case "supplier":
      return (
        !!context.user &&
        !!context.business?.id &&
        !!context.business.Supplier?.id
      );
    case "businessMember":
      return (
        !!context.user &&
        !!context.business?.id &&
        !!context.business.BusinessUser
      );
    case "buyerMember":
      return (
        !!context.user &&
        !!context.business?.id &&
        !!context.business.Retailer?.id &&
        !!context.business.BusinessUser
      );
    case "supplierMember":
      return (
        !!context.user &&
        !!context.business?.id &&
        !!context.business.Supplier?.id &&
        !!context.business.BusinessUser
      );
    default: {
      const exhaustive: never = context;
      return exhaustive;
    }
  }
};
